import { HELP_CENTER_LINK } from 'constants/links';
import {
    WIDGET_ACCOUNT_ID_PLACEHOLDER,
    WIDGET_DISPLAY_CODE_PLACEHOLDER,
    WIDGET_LIBRARY_CODE_PLACEHOLDER,
    WidgetPlatform,
} from 'constants/widgets';

interface WidgetStrategy {
    platform: WidgetPlatform;
    name: string;
    image?: string;
    title: string;
    helpCenterArticleUrl?: string;
    platformSetup: string[];
    platformSetupVideoEmbedSrc?: string;
    widgetSetup?: string[];
}

export const widgetStrategies: WidgetStrategy[] = [
    {
        platform: WidgetPlatform.wordpress,
        name: 'WordPress',
        image: '/images/logos/wordpress.png',
        title: 'Installing the widget library to Wordpress',
        helpCenterArticleUrl: `${HELP_CENTER_LINK}/articles/7141647-how-to-use-givebutter-widgets-on-a-wordpress-website`,
        platformSetupVideoEmbedSrc:
            'https://www.loom.com/embed/e923f81ceb1b4129bf63aba55db39b37?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true',
        platformSetup: [
            'Log in to your WordPress account.',
            'Install and activate <a target="_blank" href="https://wordpress.org/plugins/givebutter/">Givebutter Widgets plugin</a>.',
            'On the left-hands side of your dashboard, click on Settings > Givebutter Widgets.',
            'Paste the code below on the Account ID and click "Save Changes".',
            WIDGET_ACCOUNT_ID_PLACEHOLDER,
        ],
        widgetSetup: [
            'In the WordPress dashboard, select Pages from the left-hand navigation menu.',
            "Select the page you'd like to embed the widget on.",
            'Once in the editor, click the "+" button to add a new block. Select "Custom HTML". You may need to search for this block.',
            'Copy and paste the above code into the HTML block and click "Update".',
            'Alternatively, you can utilize the shortcode below to embed the widget.',
            WIDGET_DISPLAY_CODE_PLACEHOLDER,
        ],
    },
    {
        platform: WidgetPlatform.squarespace,
        name: 'Squarespace',
        image: '/images/logos/squarespace.png',
        title: 'Installing the widget library to Squarespace',
        helpCenterArticleUrl: `${HELP_CENTER_LINK}/articles/7198277-how-to-use-givebutter-widgets-on-a-squarespace-website`,
        platformSetupVideoEmbedSrc:
            'https://www.loom.com/embed/7277ee6092824679b269f968dfd3e3d7?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true',
        platformSetup: [
            "Click on the <span style='font-weight: 500; font-style: italic;'>Website</span> heading in your Squarespace account.",
            "Scroll down, and under the <span style='font-weight: 500; font-style: italic;'>Utilities</span> heading, click <span style='font-weight: 500; font-style: italic;'>Website Tools</span>.",
            "Click <span style='font-weight: 500; font-style: italic;'>Code Injection</span>.",
            "Under the <span style='font-weight: 500; font-style: italic;'>Header</span> section, paste the library code below, and save.",
            WIDGET_LIBRARY_CODE_PLACEHOLDER,
        ],
        widgetSetup: [
            'Select "Pages" from the main dashboard and navigate to the specific page where you want to add the widget.',
            'Click the "+ Add" icon to add a new block and select "Code" from the menu. Copy the code above for your created widget and paste it into the code block and save.',
        ],
    },
    {
        platform: WidgetPlatform.wix,
        name: 'Wix',
        image: '/images/logos/wix.png',
        title: 'Installing the widget library to Wix',
        helpCenterArticleUrl: `${HELP_CENTER_LINK}/articles/6464972-how-to-use-givebutter-widgets-on-a-wix-website`,
        platformSetupVideoEmbedSrc:
            'https://www.loom.com/embed/7a591a2af9a94c229db8c83b5548512a?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true',
        platformSetup: [
            'Wix does not require specific platform settings. Each embedded widget will have its own setup instructions.',
        ],
        widgetSetup: [
            "Log in to your Wix account and select the website you would like to edit on your site's dashboard.",
            "Navigate to the 'Add element' menu which is found by pressing the 'plus' icon inside of the editor.",
            "Inside the 'Add to Site' menu, you'll find an option labeled 'Embed Code', select it and from list, drag and drop a 'Custom element' to your preferred location on the webpage.",
            "When you've placed the 'Custom Element' onto your site, double-click it and copy the following library code into the “Server URL” field:",
            WIDGET_LIBRARY_CODE_PLACEHOLDER,
            "In the “Tag name” field type in <span style='font-weight: 500; font-style: italic;'>'givebutter-widget'</span>",
            "When you're ready to embed a widget, hover over your 'Custom element' and click 'Set Attributes'.",
            "In the “Attribute name” field type in <span style='font-weight: 500; font-style: italic;'>'id'</span> and copy the widget's ID into the 'Value' input field and save.",
            WIDGET_DISPLAY_CODE_PLACEHOLDER,
        ],
    },

    {
        platform: WidgetPlatform.webflow,
        name: 'Webflow',
        image: '/images/logos/webflow.png',
        title: 'Installing the widget library to Webflow',
        platformSetup: [
            'Log in to your Webflow account.',
            'Navigate to Settings > Custom Code within the project where you want to add the code.',
            'Scroll down to the "Head Code" section and paste the code below in the the input field and save.',
            WIDGET_LIBRARY_CODE_PLACEHOLDER,
        ],
        widgetSetup: [
            'On the page where you want to embed the custom code, add an "Embed" element. This is found in the "Elements" panel under the "Components" category.',
            'Click on the "Embed" element to open the code editor, paste in your widget\'s code above and save.',
        ],
    },
    {
        platform: WidgetPlatform.godaddy,
        name: 'GoDaddy',
        image: '/images/logos/godaddy.png',
        title: 'Installing the widget library to GoDaddy',
        platformSetup: [
            'Log in to your GoDaddy account.',
            'Scroll to Websites + Marketing and select Manage next to your website to open your site.',
            'Select Edit Website or Edit Site to open your website builder.',
            'Go to the page and location you want to add your custom code, and add a section.',
            'Search for HTML section and select Add. Past the code below in the the Custom Code field.',
            WIDGET_LIBRARY_CODE_PLACEHOLDER,
        ],
        widgetSetup: [
            "Click on the HTML section where you've inserted the library code, and paste your widget's code immediately below it.",
        ],
    },
    {
        platform: WidgetPlatform.weebly,
        name: 'Weebly',
        image: '/images/logos/weebly.png',
        title: 'Installing the widget library to Weebly',
        helpCenterArticleUrl: `${HELP_CENTER_LINK}/articles/7203871-how-to-use-givebutter-widgets-on-a-weebly-website`,
        platformSetupVideoEmbedSrc:
            'https://www.loom.com/embed/89dc0e096bc643b3b684e24148c6806a?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true',
        platformSetup: [
            'Log in to your Weebly account.',
            'Navigate to Settings > SEO > Header code.',
            'Copy and paste the code below in the the Header Type text field.',
            WIDGET_LIBRARY_CODE_PLACEHOLDER,
        ],
        widgetSetup: [
            'On the left sidebar, find the "Embed Code" element under the "Basic" section. Click on it, drag it to where you want your widget to appear.',
            'Click on "Click to set custom HTML", paste your widget\'s HTML code into the provided space and save.',
        ],
    },
    {
        platform: WidgetPlatform.other,
        name: 'other websites',
        title: 'Installing the widget library to a website',
        helpCenterArticleUrl: `${HELP_CENTER_LINK}/articles/6464859-how-to-use-givebutter-widgets-on-your-website`,
        platformSetup: [
            'To start adding donation buttons and forms onto your website, copy and paste the code below into the <strong>&lt;head&gt;&lt;/head&gt;</strong> tag of your website.',
            WIDGET_LIBRARY_CODE_PLACEHOLDER,
            "When the head code has been added, simply create a widget and copy the widget's code onto any page of your website, enhancing your organization's ability to receive donations directly where your donors are located.",
        ],
        widgetSetup: ['Copy the code snippet above and place it anywhere on your HTML page.'],
    },
];

export type { WidgetStrategy };
